import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/apiConfig';

const LocationContext = createContext();

const initialState = {
  locations: [],
  selectedLocation: null,
  loading: false,
  error: null,
};

const locationReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_LOCATIONS':
      return {
        ...state,
        locations: action.payload,
        loading: false,
        error: null,
      };
    case 'SET_SELECTED_LOCATION':
      localStorage.setItem('selectedLocationId', action.payload?._id || '');
      return {
        ...state,
        selectedLocation: action.payload,
      };
    case 'ADD_LOCATION':
      return {
        ...state,
        locations: [...state.locations, action.payload],
      };
    case 'UPDATE_LOCATION':
      return {
        ...state,
        locations: state.locations.map(location =>
          location._id === action.payload._id ? action.payload : location
        ),
        selectedLocation: state.selectedLocation?._id === action.payload._id 
          ? action.payload 
          : state.selectedLocation,
      };
    case 'DELETE_LOCATION':
      return {
        ...state,
        locations: state.locations.filter(location => location._id !== action.payload),
        selectedLocation: state.selectedLocation?._id === action.payload 
          ? (state.locations.length > 1 ? state.locations[0] : null)
          : state.selectedLocation,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

export const LocationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(locationReducer, initialState);
  const { isAuthenticated, user } = useAuth();

  // Load locations when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('User authenticated, loading locations...');
      loadLocations();
    } else {
      console.log('User not authenticated, resetting locations');
      dispatch({ type: 'RESET' });
    }
  }, [isAuthenticated, user]);

  // Set default selected location
  useEffect(() => {
    if (state.locations.length > 0 && !state.selectedLocation) {
      const savedLocationId = localStorage.getItem('selectedLocationId');
      
      console.log('Setting default location with:', {
        availableLocations: state.locations,
        savedLocationId,
        userDefaultLocationId: user?.preferences?.dashboard?.defaultLocation
      });
      
      // Find location by ID with string comparison (MongoDB ObjectIds need toString())
      const savedLocation = savedLocationId ? 
        state.locations.find(loc => loc._id && savedLocationId && loc._id.toString() === savedLocationId.toString()) : null;
      
      // If saved location not found, use user's default location or first available
      const defaultLocation = savedLocation || 
        (user?.preferences?.dashboard?.defaultLocation ? 
          state.locations.find(loc => 
            loc._id && user.preferences.dashboard.defaultLocation && 
            loc._id.toString() === user.preferences.dashboard.defaultLocation.toString()
          ) : state.locations[0]);
      
      console.log('Selected location:', defaultLocation);
      
      if (defaultLocation) {
        dispatch({
          type: 'SET_SELECTED_LOCATION',
          payload: defaultLocation,
        });
      }
    }
  }, [state.locations, user]);

  const loadLocations = async () => {
    console.log('Loading locations...');
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await api.get('/locations');
      console.log('Locations API response:', response.data);
      
      if (response.data && response.data.success) {
        // Get locations from response - should be an array from server
        const serverLocations = response.data.data?.locations || [];
        console.log('Server returned locations:', serverLocations);
        
        // Ensure each location has a string _id for consistent comparison
        const processedLocations = serverLocations.map(loc => {
          return {
            ...loc,
            _id: loc._id ? loc._id.toString() : null
          };
        });
        
        console.log('Processed locations:', processedLocations);
        dispatch({
          type: 'SET_LOCATIONS',
          payload: processedLocations
        });
        
        // Note: Don't auto-select here - let the second useEffect handle it
        // to avoid double selection and duplicate API calls
      } else {
        console.error('API returned success: false or missing data structure');
        dispatch({
          type: 'SET_ERROR',
          payload: response.data?.message || 'Failed to load locations: Invalid response format',
        });
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error.response?.data?.message || 'Failed to load locations',
      });
    }
  };

  const selectLocation = (location) => {
    dispatch({
      type: 'SET_SELECTED_LOCATION',
      payload: location,
    });
  };

  const addLocation = async (locationData) => {
    try {
      const response = await api.post('/locations', locationData);
      dispatch({
        type: 'ADD_LOCATION',
        payload: response.data.data.location,
      });
      return { success: true, location: response.data.data.location };
    } catch (error) {
      // If user lacks permissions but also has zero locations, try first-location endpoint
      const status = error.response?.status;
      const apiMessage = error.response?.data?.message || error.response?.data?.error;
      if (status === 403 && (state.locations?.length || 0) === 0) {
        try {
          const payload = {
            name: locationData.name,
            type: (locationData.type || 'WAREHOUSE').toUpperCase(),
          };
          const resp2 = await api.post('/locations/first', payload);
          const created = resp2.data?.data?.location;
          dispatch({
            type: 'ADD_LOCATION',
            payload: created,
          });
          // Also set it as selected immediately
          dispatch({ type: 'SET_SELECTED_LOCATION', payload: created });
          return { success: true, location: created };
        } catch (e2) {
          const msg2 = e2.response?.data?.message || 'Failed to create first location';
          dispatch({ type: 'SET_ERROR', payload: msg2 });
          return { success: false, error: msg2 };
        }
      }

      const errorMessage = apiMessage || 'Failed to add location';
      dispatch({
        type: 'SET_ERROR',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const updateLocation = async (locationId, updateData) => {
    try {
      const response = await api.put(`/locations/${locationId}`, updateData);
      dispatch({
        type: 'UPDATE_LOCATION',
        payload: response.data.data.location,
      });
      return { success: true, location: response.data.data.location };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update location';
      dispatch({
        type: 'SET_ERROR',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const deleteLocation = async (locationId) => {
    try {
      await api.delete(`/locations/${locationId}`);
      dispatch({
        type: 'DELETE_LOCATION',
        payload: locationId,
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete location';
      dispatch({
        type: 'SET_ERROR',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value = {
    ...state,
    selectLocation,
    addLocation,
    updateLocation,
    deleteLocation,
    loadLocations,
    clearError,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};
